import React, { useState, useMemo, useCallback, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Button, Input, Text } from "@teals/ui-web";
import {
  useGetDataSourcesQuery,
  useDeleteDataSourceMutation,
  useUpdateAgentAssociationsMutation,
  type DataSource,
  type DsType,
} from "../../store/api/dataSourcesApi";
import { DS_TYPE_LABELS } from "./constants";
import {
  PageWrapper,
  Header,
  HeaderRow,
  Subtitle,
  SearchRow,
  FilterBadge,
  Grid,
  EmptyState,
  ErrorBanner,
} from "./styles";
import DataSourceCard from "./DataSourceCard";
import DataSourceDetail from "./DataSourceDetail";
import CreateWizard from "./CreateWizard/CreateWizard";
import { API_BASE_URL } from "../../utils/api";

function agentIdFromReturnTo(returnTo: string | null): string | null {
  if (!returnTo) return null;
  const match = returnTo.match(/\/agents\/([^/?#]+)/);
  return match?.[1] ? decodeURIComponent(match[1]) : null;
}

const DataSources: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const returnTo = searchParams.get("returnTo");
  const openCreate = searchParams.get("create") === "1";
  const returnTab = searchParams.get("tab");

  const { data: dataSources, isLoading, error } = useGetDataSourcesQuery();
  const [deleteDs] = useDeleteDataSourceMutation();
  const [updateAgentAssociations] = useUpdateAgentAssociationsMutation();

  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState<DsType | null>(null);

  const [wizardOpen, setWizardOpen] = useState(openCreate);
  const [selectedDs, setSelectedDs] = useState<DataSource | null>(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailTab, setDetailTab] = useState<"overview" | "upload">("overview");
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);

  useEffect(() => {
    if (openCreate) setWizardOpen(true);
  }, [openCreate]);

  const filteredSources = useMemo(() => {
    if (!dataSources) return [];
    return dataSources.filter((ds) => {
      const matchesSearch =
        !search ||
        ds.name.toLowerCase().includes(search.toLowerCase()) ||
        ds.dataSourceId.toLowerCase().includes(search.toLowerCase());
      const matchesType = !typeFilter || ds.dsType === typeFilter;
      return matchesSearch && matchesType;
    });
  }, [dataSources, search, typeFilter]);

  const openDetail = useCallback(
    (ds: DataSource, tab: "overview" | "upload" = "overview") => {
      setSelectedDs(ds);
      setDetailTab(tab);
      setDetailOpen(true);
      setDeleteConfirm(null);
    },
    [],
  );

  const closeDetail = useCallback(() => {
    setDetailOpen(false);
    setSelectedDs(null);
    setDeleteConfirm(null);
  }, []);

  const handleDelete = async (dsId: string) => {
    try {
      await deleteDs(dsId).unwrap();
      setDeleteConfirm(null);
      if (selectedDs?.dataSourceId === dsId) closeDetail();
    } catch {
      /* RTK error state handles display */
    }
  };

  const clearCreateParams = () => {
    const next = new URLSearchParams(searchParams);
    next.delete("create");
    setSearchParams(next, { replace: true });
  };

  const navigateBackToAgent = async (ds?: DataSource) => {
    if (!returnTo) return false;
    const agentId = agentIdFromReturnTo(returnTo);
    if (agentId && ds) {
      try {
        const nextIds = ds.agentIds.includes(agentId)
          ? ds.agentIds
          : [...ds.agentIds, agentId];
        await updateAgentAssociations({
          id: ds.dataSourceId,
          agentIds: nextIds,
        }).unwrap();
      } catch {
        /* association may fail if DS not yet linkable; still return */
      }
    }
    const target =
      returnTab && !returnTo.includes("tab=")
        ? `${returnTo}${returnTo.includes("?") ? "&" : "?"}tab=${encodeURIComponent(returnTab)}`
        : returnTo;
    navigate(target);
    return true;
  };

  return (
    <PageWrapper>
      <Header>
        <HeaderRow>
          <Text variant="h2">Data Sources</Text>
          <Button
            variant="primary"
            onClick={() => {
              setWizardOpen(true);
              const next = new URLSearchParams(searchParams);
              next.set("create", "1");
              setSearchParams(next, { replace: true });
            }}
          >
            + Create Data Source
          </Button>
        </HeaderRow>
        <Subtitle>Configure retrieval context for your agents</Subtitle>
      </Header>

      <SearchRow>
        <Input
          placeholder="Search data sources…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{ flex: 1, maxWidth: 280 }}
        />
        {(Object.keys(DS_TYPE_LABELS) as DsType[]).map((t) => (
          <FilterBadge
            key={t}
            variant="info"
            size="sm"
            $active={typeFilter === t}
            onClick={() => setTypeFilter(typeFilter === t ? null : t)}
          >
            {DS_TYPE_LABELS[t]}
          </FilterBadge>
        ))}
      </SearchRow>

      {error && (
        <ErrorBanner>
          Failed to load data sources. Please try again.
        </ErrorBanner>
      )}

      {isLoading ? (
        <EmptyState>Loading data sources…</EmptyState>
      ) : filteredSources.length === 0 ? (
        <EmptyState>
          {dataSources?.length === 0
            ? 'No data sources yet. Click "Add Data Source" to get started.'
            : "No data sources match your search."}
        </EmptyState>
      ) : (
        <Grid>
          {filteredSources.map((ds) => (
            <DataSourceCard
              key={ds.dataSourceId}
              ds={ds}
              onClick={() => openDetail(ds)}
            />
          ))}
        </Grid>
      )}

      <DataSourceDetail
        ds={selectedDs}
        isOpen={detailOpen}
        onClose={closeDetail}
        initialTab={detailTab}
        deleteConfirm={deleteConfirm}
        onDeleteConfirm={setDeleteConfirm}
        onDelete={handleDelete}
      />

      <CreateWizard
        isOpen={wizardOpen}
        onClose={() => {
          setWizardOpen(false);
          clearCreateParams();
        }}
        apiBaseUrl={API_BASE_URL}
        onOpenDetail={async (ds, tab) => {
          setWizardOpen(false);
          clearCreateParams();
          const returned = await navigateBackToAgent(ds);
          if (!returned) openDetail(ds, tab);
        }}
      />
    </PageWrapper>
  );
};

export default DataSources;
